import { scopeThreadRef, scopedThreadKey } from "@cmmd-forge/client-runtime";
import { type EnvironmentId, type ThreadId, type TurnId } from "@cmmd-forge/contracts";
import { useCallback } from "react";
import { type useNavigate } from "@tanstack/react-router";

import { resolveJumpToParentTarget } from "../../lib/jumpToParent";
import { useRightPanelStore } from "../../rightPanelStore";
import { type Thread } from "../../types";

/**
 * The thread header's panel controls.
 *
 * These used to navigate in order to change panel state — every toggle was a
 * router round-trip whose only effect was rewriting a search param. Panel state
 * now lives in the store, so navigation is reserved for what it is actually
 * for: going to a different thread.
 */
export function useChatRoutePanelControls(input: {
  readonly navigate: ReturnType<typeof useNavigate>;
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly threadKey: string | null;
  readonly activeThread: Thread | undefined;
  readonly isServerThread: boolean;
  readonly canOpenRightSidebar: boolean;
  readonly canvasOpen: boolean;
  readonly diffOpen: boolean;
  readonly planOpen: boolean;
  readonly activePlanTurnId: string | null | undefined;
  readonly sidebarProposedPlanTurnId: string | null | undefined;
  readonly activeTaskContextExists: boolean;
  readonly onDiffPanelOpen: (() => void) | undefined;
  readonly markPlanAutoOpenedForTurn: (turnKey: string) => void;
}): {
  readonly onJumpToParent: () => void;
  readonly onToggleDiff: () => void;
  readonly onViewActiveTaskContext: () => void;
  readonly onTogglePreview: () => void;
  readonly onToggleCanvas: (nextOpen: boolean) => void;
  readonly onToggleCanvasInvert: () => void;
  readonly onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
  readonly togglePlanSidebar: () => void;
  readonly openPlanPanel: (
    targetThreadId: ThreadId,
    targetEnvironmentId: EnvironmentId,
  ) => Promise<void>;
} {
  const { threadKey } = input;

  const onJumpToParent = useCallback(() => {
    const target = resolveJumpToParentTarget(input.activeThread ?? null);
    if (!target) return;
    void input.navigate({
      to: "/$environmentId/$threadId",
      params: { environmentId: target.environmentId, threadId: target.threadId },
    });
  }, [input]);

  const onToggleDiff = useCallback(() => {
    if (!input.isServerThread || !threadKey) return;
    const store = useRightPanelStore.getState();
    if (input.diffOpen) {
      store.closeSurface(threadKey, "diff");
      return;
    }
    input.onDiffPanelOpen?.();
    store.openDiff(threadKey);
  }, [input, threadKey]);

  const onViewActiveTaskContext = useCallback(() => {
    if (!input.isServerThread || !input.activeTaskContextExists || !threadKey) return;
    useRightPanelStore.getState().open(threadKey, "task");
  }, [input, threadKey]);

  const onTogglePreview = useCallback(() => {
    if (!input.canOpenRightSidebar || !threadKey) return;
    useRightPanelStore.getState().toggleVisibility(threadKey);
  }, [input.canOpenRightSidebar, threadKey]);

  const onToggleCanvas = useCallback(
    (nextOpen: boolean) => {
      if (!input.isServerThread || !threadKey) return;
      const store = useRightPanelStore.getState();
      if (nextOpen) store.open(threadKey, "canvas");
      else store.closeSurface(threadKey, "canvas");
    },
    [input.isServerThread, threadKey],
  );

  const onToggleCanvasInvert = useCallback(() => {
    onToggleCanvas(!input.canvasOpen);
  }, [input.canvasOpen, onToggleCanvas]);

  const onOpenTurnDiff = useCallback(
    (turnId: TurnId, filePath?: string) => {
      if (!input.isServerThread || !threadKey) return;
      input.onDiffPanelOpen?.();
      useRightPanelStore.getState().openDiff(threadKey, turnId, filePath ?? null);
    },
    [input, threadKey],
  );

  const togglePlanSidebar = useCallback(() => {
    if (!threadKey) return;
    const store = useRightPanelStore.getState();
    if (input.planOpen) {
      store.closeSurface(threadKey, "plan");
      return;
    }
    // Claim the turn so the auto-open effect does not immediately reopen a
    // panel the user just dismissed, or reopen this one a second time.
    const turnKey = input.activePlanTurnId ?? input.sidebarProposedPlanTurnId ?? "__user_opened__";
    input.markPlanAutoOpenedForTurn(turnKey);
    store.open(threadKey, "plan");
  }, [input, threadKey]);

  const openPlanPanel = useCallback(
    async (targetThreadId: ThreadId, targetEnvironmentId: EnvironmentId) => {
      // The one control that genuinely navigates: it opens a plan beside a
      // *different* thread, so the surface is keyed to that thread, not this one.
      useRightPanelStore
        .getState()
        .open(scopedThreadKey(scopeThreadRef(targetEnvironmentId, targetThreadId)), "plan");
      await input.navigate({
        to: "/$environmentId/$threadId",
        params: { environmentId: targetEnvironmentId, threadId: targetThreadId },
      });
    },
    [input],
  );

  return {
    onJumpToParent,
    onToggleDiff,
    onViewActiveTaskContext,
    onTogglePreview,
    onToggleCanvas,
    onToggleCanvasInvert,
    onOpenTurnDiff,
    togglePlanSidebar,
    openPlanPanel,
  };
}
